import { useNavigate, useLocation } from 'react-router-dom';
import { createElement } from 'react';
import NotificationBell from '@/components/notifications/NotificationBell';
import {
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/react';
import { Settings, LogOut, User, Lock } from 'lucide-react';
import { AppModule } from '@estimator/shared';
import { useAuthStore } from '@/store/authStore';
import { useModuleStore } from '@/stores/moduleStore';
import { MODULE_NAV, MODULE_ICONS, MODULE_LABELS } from '@/config/modules';

interface Props {
  showUserMenu?: boolean;
}

export default function TopBar({ showUserMenu = false }: Props) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { activeModule, setActiveModule } = useModuleStore();
  const location = useLocation();
  // The home/calendar route belongs to no module — highlight Anvil, not a module tab.
  const onHome = location.pathname === '/';
  const modules = Object.values(AppModule);

  const userInitials = user?.name
    ? user.name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : '??';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className="flex items-center h-11 px-4 border-b shrink-0"
      style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)' }}
    >
      <span
        className="text-sm font-semibold tracking-tight font-ui mr-6 cursor-pointer px-2 py-1 rounded-md transition-colors"
        style={{
          color: 'var(--accent)',
          backgroundColor: onHome ? 'var(--accent-muted, rgba(100,255,218,0.08))' : undefined,
        }}
        onClick={() => navigate('/')}
      >
        Anvil
      </span>
      <div className="flex items-center gap-1">
        {modules.map((mod) => {
          const isActiveModule = !onHome && activeModule === mod;
          const IconComponent = MODULE_ICONS[mod];
          return (
            <button
              key={mod}
              onClick={() => {
                setActiveModule(mod);
                const firstPage = MODULE_NAV[mod][0]?.items[0]?.path;
                if (firstPage) navigate(firstPage);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-ui rounded-md transition-colors ${
                isActiveModule ? 'font-medium' : 'hover:bg-[var(--bg-panel)]'
              }`}
              style={{
                color: isActiveModule ? 'var(--accent)' : 'var(--text-muted)',
                backgroundColor: isActiveModule ? 'var(--accent-muted, rgba(100,255,218,0.08))' : undefined,
              }}
            >
              {createElement(IconComponent, { size: 14 })}
              {MODULE_LABELS[mod]}
            </button>
          );
        })}
      </div>

      <div className="ml-auto flex items-center gap-1">
        <NotificationBell />
        {showUserMenu && (
          <div>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <button className="flex items-center gap-2 rounded-lg p-1 hover:bg-[var(--bg-panel)] transition-colors">
                <Avatar
                  size="sm"
                  name={userInitials}
                  classNames={{ base: 'cursor-pointer border border-[var(--border-em)] shrink-0', name: 'text-xs font-ui' }}
                  style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--accent)' }}
                />
                <span className="text-xs font-medium hidden sm:block" style={{ color: 'var(--text-primary)' }}>
                  {user?.name ?? 'Unknown'}
                </span>
              </button>
            </DropdownTrigger>
            <DropdownMenu aria-label="User menu" variant="flat" classNames={{ base: 'border border-[var(--border)]' }} style={{ backgroundColor: 'var(--bg-panel)' }}>
              <DropdownItem key="profile" startContent={<User size={14} />} textValue="Profile" style={{ color: 'var(--text-primary)' }}>
                Profile
              </DropdownItem>
              <DropdownItem key="change-password" startContent={<Lock size={14} />} textValue="Change Password" style={{ color: 'var(--text-primary)' }} onPress={() => navigate('/change-password')}>
                Change Password
              </DropdownItem>
              <DropdownItem key="settings" startContent={<Settings size={14} />} textValue="Settings" style={{ color: 'var(--text-primary)' }} onPress={() => navigate('/settings')}>
                Settings
              </DropdownItem>
              <DropdownItem key="logout" color="danger" startContent={<LogOut size={14} />} onPress={handleLogout} textValue="Log out">
                Log out
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
}
